import React, { useEffect, useState } from 'react';
import { getNewsUpcoming } from '../api';

type NewsEvent = {
  title: string;
  currency: string;
  impact: string;
  event_time: string;
  actual: string | null;
  forecast: string | null;
  previous: string | null;
};

type CurrentBlackout = {
  in_blackout: boolean;
  event: string | null;
  minutes_to_event: number | null;
  window: string | null;
};

type NewsResponse = {
  current_blackout: CurrentBlackout;
  upcoming: NewsEvent[];
};

function formatMinutes(mins: number): string {
  const abs = Math.abs(mins);
  if (abs >= 60) {
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${abs} min`;
}

function minutesUntil(iso: string): number {
  const t = new Date(iso).getTime();
  return Math.round((t - Date.now()) / 60000);
}

function formatLocal(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function NewsBanner() {
  const [data, setData] = useState<NewsResponse | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await getNewsUpcoming(24);
        if (!cancelled) setData(r.data);
      } catch (e) {
        // silent - banner hides on missing data
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!data) return null;

  const cb = data.current_blackout;
  const upcoming = data.upcoming || [];

  // STATE A: active blackout
  if (cb?.in_blackout) {
    const mins = cb.minutes_to_event;
    let timing = '';
    if (mins != null) {
      timing = mins >= 0 ? `in ${formatMinutes(mins)}` : `${formatMinutes(mins)} ago`;
    }
    return (
      <div className="bg-red-900/40 border-b border-red-700 px-4 py-2 flex items-center justify-between gap-4 text-sm">
        <div className="flex items-center gap-2 text-red-300 font-bold whitespace-nowrap">
          <span>🔴</span>
          <span>NEWS BLACKOUT ACTIVE</span>
        </div>
        <div className="flex-1 text-center text-red-100 font-bold truncate">
          {cb.event || 'High-impact event'}{timing ? ` ${timing}` : ''}
          <span className="ml-2 text-red-300/80 font-normal text-xs">· trades auto-skipped</span>
        </div>
        <div className="text-red-300 text-xs whitespace-nowrap">
          {cb.window ? `Window: ${cb.window}` : ''}
        </div>
      </div>
    );
  }

  // STATE C: nothing upcoming
  if (upcoming.length === 0) return null;

  // STATE B: upcoming events
  const next = upcoming.slice(0, expanded ? upcoming.length : 2);
  return (
    <div
      className="bg-yellow-900/30 border-b border-yellow-700 px-4 py-2 text-sm cursor-pointer hover:bg-yellow-900/40 transition-colors"
      onClick={() => setExpanded(v => !v)}
      title={expanded ? 'Click to collapse' : 'Click to expand'}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-yellow-300 font-bold whitespace-nowrap">📰 News</span>
        {next.map((ev, i) => {
          const m = minutesUntil(ev.event_time);
          const when = m >= 0 ? `in ${formatMinutes(m)}` : `${formatMinutes(m)} ago`;
          return (
            <span key={i} className="text-yellow-100 text-xs whitespace-nowrap">
              {i === 0 ? 'Next: ' : '· '}
              <span className="font-semibold">{ev.title}</span>
              <span className="text-yellow-300/80"> {when}</span>
              {expanded && (
                <span className="text-yellow-400/60 ml-1">({formatLocal(ev.event_time)})</span>
              )}
            </span>
          );
        })}
        <span className="text-yellow-400/60 text-xs ml-auto">
          {upcoming.length} event{upcoming.length === 1 ? '' : 's'} in next 24h {expanded ? '▲' : '▼'}
        </span>
      </div>
    </div>
  );
}
